import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The admin panel's one table idiom, plus the two hooks every list in the module needs.
 *
 * This is deliberately NOT a generic `<DataTable columns={…} data={…} />` abstraction: the four
 * tables here have genuinely different cells (a role `Select`, an expand chevron, a spend/budget
 * pair) and forcing them through a column-config API buys nothing but indirection. What they DO
 * share — sticky dense chrome, sort semantics, and knowing how much room they actually have — is
 * shared here. The chrome itself lives in org-admin-tokens.css under `.oa-table`.
 */

// ── Container measurement ───────────────────────────────────────────────────

/**
 * The width of the element itself, not of the window.
 *
 * The admin panel renders inside a shell whose sidebar collapses, so a `lg:` viewport breakpoint
 * describes something other than the space the table has. Every layout switch in the module keys
 * off this instead. Paired with `container-type: inline-size` on `.oa-route` for the pure-CSS
 * switches; this hook covers the ones that need to change the *markup* (a table becoming cards),
 * which CSS alone cannot do.
 */
export function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    // Seed synchronously so the first paint is already at the right layout — waiting for the
    // observer's first callback renders one frame of the wrong variant.
    setWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

/**
 * The container width below which a multi-column table is stacked into cards instead.
 *
 * Set from the tables themselves, not from a device: the roster's fixed columns (status, authority,
 * role, actions) come to ~450px, so below ~720px the flexible name column drops under ~270px and
 * starts truncating ordinary addresses. That is the point at which columns stop earning their keep.
 */
export const OA_NARROW_BREAKPOINT = 720;

/**
 * The container width below which a two-up row of cards stacks into one column.
 *
 * Higher than `OA_NARROW_BREAKPOINT` because two cards share this width: at 900px each column is
 * ~444px, which is about the narrowest a ranked bar chart or a two-column table still reads at.
 *
 * Lives here, beside the other breakpoint, because the Overview and Analysis tabs both use it and
 * must break at the SAME real width — a two-up that stacks on one tab and not the other at the same
 * panel size is the inconsistency this constant exists to prevent.
 */
export const OA_TWO_UP_BREAKPOINT = 900;

// ── Sorting ─────────────────────────────────────────────────────────────────

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

export function useSort<K extends string>(initial: SortState<K>) {
  const [sort, setSort] = useState<SortState<K>>(initial);

  const toggle = useCallback((key: K) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : // A new column starts in the direction that puts the interesting rows first: descending
          // for the numbers an admin scans for outliers, ascending for names they scan for a match.
          { key, direction: 'asc' },
    );
  }, []);

  return { sort, setSort, toggle };
}

/**
 * Sorts by an accessor, keeping the comparison type-correct: `localeCompare` for strings (so
 * "Álvaro" sorts where a reader expects, not after "Z"), numeric comparison for numbers, and
 * nullish values always last regardless of direction — a member with no role should not float to
 * the top of an ascending sort just because `null` compares low.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K>,
  accessor: (row: T, key: K) => string | number | null | undefined,
): T[] {
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = accessor(a, sort.key);
    const right = accessor(b, sort.key);
    const leftMissing = left === null || left === undefined || left === '';
    const rightMissing = right === null || right === undefined || right === '';
    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;
    if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
    return String(left).localeCompare(String(right), undefined, { numeric: true }) * factor;
  });
}

// ── Column header ───────────────────────────────────────────────────────────

export interface SortableHeaderProps<K extends string> {
  columnKey: K;
  label: string;
  sort: SortState<K>;
  onSort: (key: K) => void;
  align?: 'left' | 'right';
  /** Fixed column width, e.g. `'1%'` for a chevron column that should not claim space. */
  width?: string;
  className?: string;
}

export function SortableHeader<K extends string>({
  columnKey,
  label,
  sort,
  onSort,
  align = 'left',
  width,
  className,
}: SortableHeaderProps<K>) {
  const isActive = sort.key === columnKey;
  const Icon = !isActive ? ChevronsUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;

  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      // `aria-sort` is what tells a screen reader the table is sorted and by which column. Only the
      // active column may carry a value other than "none".
      aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(align === 'right' && 'oa-num', className)}
    >
      <button
        type="button"
        className={cn('oa-th-sort', align === 'right' && 'justify-end')}
        onClick={() => onSort(columnKey)}
      >
        {label}
        <Icon
          className={cn('h-3 w-3 shrink-0', isActive ? 'opacity-100' : 'opacity-0')}
          aria-hidden
        />
      </button>
    </th>
  );
}

/** A non-sortable header cell, so every table uses the same element and padding. */
export function PlainHeader({
  label,
  align = 'left',
  width,
  srOnly,
  className,
}: {
  label: string;
  align?: 'left' | 'right';
  width?: string;
  /** For action/expander columns: the column still needs a name for assistive tech. */
  srOnly?: boolean;
  className?: string;
}) {
  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={cn(align === 'right' && 'oa-num', className)}
    >
      <span className={srOnly ? 'sr-only' : undefined}>{label}</span>
    </th>
  );
}

// ── Text search ─────────────────────────────────────────────────────────────

/**
 * Case-insensitive substring match across the fields a row is findable by. Kept as a hook so the
 * memoisation is consistent across the four lists that filter, and so "what is searchable" is one
 * decision per list rather than an inline `.filter` that drifts.
 */
export function useTextFilter<T>(rows: readonly T[], query: string, fields: (row: T) => (string | null | undefined)[]) {
  return useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows as T[];
    return rows.filter((row) =>
      fields(row).some((value) => value?.toLowerCase().includes(needle)),
    );
    // `fields` is an inline arrow at every call site; depending on it would defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query]);
}
